import { type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import theme from '../../theme/theme';

export type VisibleField = {
  /** Stable React key — always f.key ?? f.labelKey, never the runtime label. */
  id: string;
  label: string;
  /**
   * Custom node rendered in place of `value` (e.g. a status badge). When set, the
   * `value`/empty-state text is ignored for this field. Additive — fields without
   * a node render exactly as before.
   */
  node?: ReactNode;
  /**
   * null means "no data from the backend".
   *
   * We keep null distinct from an empty string so that FieldItem can apply
   * a different visual style (dimmed colour) to missing values rather than
   * mixing display logic into the data-resolution layer (resolveField).
   * The '-' placeholder is rendered once here, not scattered across callers.
   */
  value: string | null;
  /** When true, the field spans both columns */
  fullWidth?: boolean;
  /**
   * When true, the entry renders as an empty grid cell (no label/value) — a
   * layout filler used to control where the next field wraps in a multi-column
   * grid. Ignored in `stack` mode.
   */
  spacer?: boolean;
};

/**
 * Renders a resolved set of label/value pairs in a CSS grid (2 columns by default).
 * `columns` sets the column count; `fullWidth` fields span the whole row (useful for
 * long text like addresses). `stack` forces a single-column vertical list (no grid).
 */
export function FieldGrid({
  fields,
  stack = false,
  columns = 2,
}: {
  fields: VisibleField[];
  stack?: boolean;
  columns?: number;
}) {
  if (stack) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields
          .filter(f => !f.spacer)
          .map(f => (
            <FieldItem key={f.id} label={f.label} value={f.value} node={f.node} />
          ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 2,
      }}
    >
      {fields.map(f =>
        f.spacer ? (
          <Box key={f.id} aria-hidden />
        ) : (
          <Box key={f.id} sx={f.fullWidth ? { gridColumn: '1 / -1' } : {}}>
            <FieldItem label={f.label} value={f.value} node={f.node} />
          </Box>
        ),
      )}
    </Box>
  );
}

/**
 * Single label + value pair.
 *
 * Empty-state strategy: value is null (or '') when the backend returned nothing.
 * We render '-' here — the single source of truth for the placeholder — and dim
 * it with text.label so users can visually distinguish "not filled in" from real
 * data without the resolver layer needing to know anything about presentation.
 */
function FieldItem({
  label,
  value,
  node,
}: {
  label: string;
  value: string | null;
  node?: ReactNode;
}) {
  const isEmpty = value === null || value === '';
  return (
    <Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.label, mb: 0.5 }}>
        {label}
      </Typography>
      {node !== undefined ? (
        <Box sx={{ mt: 0.25 }}>{node}</Box>
      ) : (
        <Typography
          variant="body1"
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            color: isEmpty ? theme.palette.text.label : theme.palette.text.body,
          }}
        >
          {isEmpty ? '-' : value}
        </Typography>
      )}
    </Box>
  );
}
